import { makeAutoObservable, runInAction } from 'mobx';
import { resourceStore } from '@core/stores/resource/ResourceStore';
import type { Resource } from '@core/types/resource';
import type { Aspect } from '@core/types/task/Aspect';

interface EntityContext {
  urn: string;
  type: string;
  id: string;
  title: string;
}

class EntityStore {
  currentEntity: EntityContext | null = null;
  resourceCounts: Record<string, number> = {};
  aspectCounts: Record<string, number> = {};

  resources: Resource[] = [];
  aspects: Aspect[] = [];
  isLoadingResources = false;
  isLoadingAspects = false;

  constructor() {
    makeAutoObservable(this);
  }

  loadEntity(entity: EntityContext) {
    this.currentEntity = entity;
    this.resourceCounts = {};
    this.aspectCounts = {};
    this.resources = [];
    this.aspects = [];
  }

  async loadEntityData(entity: EntityContext) {
    this.loadEntity(entity);
    this.loadResources(entity);
    this.loadAspects(entity);
  }

  async loadResources(entity: EntityContext) {
    this.isLoadingResources = true;
    try {
      const result = await resourceStore.ListLinks(entity.type, entity.id);
      runInAction(() => {
        this.resources = result.resources?.items || [];
        this.setResourceCounts({ total: this.resources.length });
      });
    } catch {
      // non-critical
    } finally {
      runInAction(() => { this.isLoadingResources = false; });
    }
  }

  async loadAspects(_entity: EntityContext) {
    this.isLoadingAspects = true;
    try {
      // Aspects come from the domain service response (embedded in entity data).
      // Future: BFF BatchGetAspects call.
      // For now, aspects are populated via setAspects() from detail pages.
    } finally {
      runInAction(() => { this.isLoadingAspects = false; });
    }
  }

  setAspects(aspects: Aspect[]) {
    this.aspects = aspects;
    this.setAspectCounts({ total: aspects.length });
  }

  clearContext() {
    this.currentEntity = null;
    this.resourceCounts = {};
    this.aspectCounts = {};
    this.resources = [];
    this.aspects = [];
    this.isLoadingResources = false;
    this.isLoadingAspects = false;
  }

  setResourceCounts(counts: Record<string, number>) {
    this.resourceCounts = counts;
  }

  setAspectCounts(counts: Record<string, number>) {
    this.aspectCounts = counts;
  }

  get totalResources(): number {
    return Object.values(this.resourceCounts).reduce((sum, c) => sum + c, 0);
  }

  get totalAspects(): number {
    return Object.values(this.aspectCounts).reduce((sum, c) => sum + c, 0);
  }

  // Optimistic operations
  addResourceOptimistic(resource: Resource) {
    this.resources = [...this.resources, resource];
    this.setResourceCounts({ total: this.resources.length });
  }

  removeResourceOptimistic(id: string) {
    this.resources = this.resources.filter(r => r.entity?.id !== id);
    this.setResourceCounts({ total: this.resources.length });
  }

  addAspectOptimistic(aspect: Aspect) {
    this.aspects = [...this.aspects, aspect];
    this.setAspectCounts({ total: this.aspects.length });
  }

  removeAspectOptimistic(id: string) {
    this.aspects = this.aspects.filter(a => a.entity?.id !== id);
    this.setAspectCounts({ total: this.aspects.length });
  }
}

export const entityStore = new EntityStore();
